import React from 'react';
import { 
  User, 
  Camera, 
  Code, 
  Mic, 
  Pencil, 
  Palette, 
  Music, 
  Video, 
  BookOpen, 
  Briefcase, 
  Brush, 
  Sparkles, 
  Headphones, 
  MessageSquare, 
  Zap, 
  Coffee, 
  Heart, 
  Smile, 
  Frown, 
  Meh 
} from 'lucide-react';
import type { AvatarData } from './AvatarGenerator';

interface AvatarDisplayProps {
  avatarData?: AvatarData;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  fallbackText?: string;
  className?: string;
}

const AvatarDisplay: React.FC<AvatarDisplayProps> = ({ 
  avatarData, 
  size = 'md', 
  fallbackText = '',
  className = ''
}) => {
  // Get the icon component based on the icon name
  const getIconComponent = (iconName?: string) => {
    switch (iconName) {
      case 'camera': return Camera;
      case 'code': return Code;
      case 'mic': return Mic;
      case 'pencil': return Pencil;
      case 'palette': return Palette;
      case 'music': return Music;
      case 'video': return Video;
      case 'book': return BookOpen;
      case 'briefcase': return Briefcase;
      case 'brush': return Brush;
      case 'sparkles': return Sparkles;
      case 'headphones': return Headphones;
      case 'messageSquare': return MessageSquare;
      case 'zap': return Zap;
      case 'coffee': return Coffee;
      case 'heart': return Heart;
      case 'smile': return Smile;
      case 'frown': return Frown;
      case 'meh': return Meh;
      default: return User;
    }
  };

  // Determine size classes
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-16 h-16',
    xl: 'w-24 h-24'
  };

  const iconSizes = {
    sm: 'h-4 w-4',
    md: 'h-5 w-5',
    lg: 'h-8 w-8',
    xl: 'h-12 w-12'
  };

  const textSizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
    xl: 'text-xl'
  };

  // If we have avatar data, render the custom avatar
  if (avatarData) {
    const IconComponent = getIconComponent(avatarData.icon);
    
    let shapeClass = 'rounded-full'; // circle by default
    
    if (avatarData.style === 'rounded-square') {
      shapeClass = 'rounded-xl';
    } else if (avatarData.style === 'hexagon') {
      shapeClass = 'clip-path-hexagon';
    } else if (avatarData.style === 'diamond') {
      shapeClass = 'rotate-45';
    } else if (avatarData.style === 'star') {
      shapeClass = 'clip-path-star';
    }
    
    return (
      <div 
        className={`flex items-center justify-center ${sizeClasses[size]} ${shapeClass} overflow-hidden ${className}`}
        style={{ backgroundColor: avatarData.bgColor }}
      >
        <div className={avatarData.style === 'diamond' ? '-rotate-45' : ''}>
          <IconComponent className={iconSizes[size]} style={{ color: avatarData.fgColor }} />
        </div>
      </div>
    );
  }
  
  // Fallback to initials or default user icon
  if (fallbackText) {
    const initials = fallbackText
      .split(' ')
      .map(name => name.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
      
    return (
      <div className={`${sizeClasses[size]} rounded-full bg-indigo-500 flex items-center justify-center text-white font-medium ${className}`}>
        <span className={textSizes[size]}>{initials}</span>
      </div>
    );
  }
  
  // Default user icon
  return (
    <div className={`${sizeClasses[size]} rounded-full bg-gray-200 dark:bg-dark-700 flex items-center justify-center text-gray-500 dark:text-gray-400 ${className}`}>
      <User className={iconSizes[size]} />
    </div>
  );
};

export default AvatarDisplay;